import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, keyframes } from '@mui/material';
import {
  Mic as MicIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';

type InboxAudioRecorderProps = {
  /** Desativa o botão de iniciar gravação (ex.: já existe arquivo selecionado ou enviando). */
  disabled?: boolean;
  /** Notifica o pai quando a gravação inicia/termina — usado só para alternar layout. */
  onRecordingChange?: (recording: boolean) => void;
  /** Recebe o arquivo de áudio finalizado. */
  onComplete: (file: File) => void;
  /** Mensagem de erro amigável (permissão de microfone, etc.). */
  onError?: (message: string) => void;
  /** Tamanho do botão de microfone quando ocioso. */
  size?: 'small' | 'medium';
  /** Botão circular verde estilo WhatsApp (composer mobile). */
  variant?: 'default' | 'whatsapp';
};

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.35); opacity: 0.55; }
`;

const wave = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Barras animadas por CSS puro — zero custo de JS por frame, ideal para máquinas fracas. */
const WAVE_BARS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const formatClock = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Gravador de áudio isolado. Mantém toda a máquina de estado de gravação
 * (MediaRecorder, stream, cronômetro) dentro de si, para que o tick de 1s
 * NÃO re-renderize o componente pai (o inbox gigante), evitando travamentos.
 */
const InboxAudioRecorder: React.FC<InboxAudioRecorderProps> = ({
  disabled = false,
  onRecordingChange,
  onComplete,
  onError,
  size = 'medium',
  variant = 'default',
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const discardRef = useRef(false);
  const chunksRef = useRef<Blob[]>([]);

  const stopTracks = useCallback(() => {
    try {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    } catch {
      /* noop */
    }
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cronômetro local: só re-renderiza este componente pequeno.
  useEffect(() => {
    if (!isRecording) return;
    timerRef.current = window.setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearTimer();
  }, [isRecording, clearTimer]);

  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  const finalizeStop = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* noop */
      }
    }
  }, []);

  const handleStart = useCallback(async () => {
    if (disabled || isRecording) return;
    try {
      discardRef.current = false;
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mimeType = mimeCandidates.find((t) => MediaRecorder.isTypeSupported(t));
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      // WebM (Chrome/Android) é streaming-friendly: um timeslice de 1s preserva os
      // metadados de duração. Já o audio/mp4 (Safari/iOS) fragmenta em chunks sem o
      // `moov atom` no início — concatenados viram um MP4 inválido que o ffmpeg do
      // servidor não decodifica, e o WhatsApp mostra "áudio não está mais disponível".
      // Nesses casos gravamos SEM timeslice para gerar um arquivo único e válido.
      const useTimeslice = (recorder.mimeType || mimeType || '').toLowerCase().includes('webm');

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        stopTracks();
        mediaRecorderRef.current = null;
        const chunks = chunksRef.current;
        chunksRef.current = [];

        if (discardRef.current) {
          discardRef.current = false;
          return;
        }

        const outType = recorder.mimeType?.split(';')[0] || 'audio/webm';
        const ext = outType.includes('mp4') ? 'm4a' : 'webm';
        const audioBlob = new Blob(chunks, { type: outType });
        if (audioBlob.size === 0) return;
        const audioFile = new File([audioBlob], `recording-${Date.now()}.${ext}`, { type: outType });
        onComplete(audioFile);
      };

      mediaRecorderRef.current = recorder;
      // Timeslice de 1s só no WebM (ver nota acima). No mp4/Safari, start() sem
      // argumento entrega o áudio num único blob válido ao parar a gravação.
      if (useTimeslice) recorder.start(1000);
      else recorder.start();
      setSeconds(0);
      setIsRecording(true);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      stopTracks();
      onError?.('Não foi possível acessar o microfone. Verifique as permissões do navegador.');
    }
  }, [disabled, isRecording, onComplete, onError, stopTracks]);

  const handleSend = useCallback(() => {
    discardRef.current = false;
    setIsRecording(false);
    finalizeStop();
  }, [finalizeStop]);

  const handleCancel = useCallback(() => {
    discardRef.current = true;
    setIsRecording(false);
    finalizeStop();
  }, [finalizeStop]);

  // Desmontagem: libera microfone e timers.
  useEffect(() => {
    return () => {
      clearTimer();
      discardRef.current = true;
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      stopTracks();
      mediaRecorderRef.current = null;
    };
  }, [clearTimer, stopTracks]);

  if (!isRecording) {
    const whatsappMicSx =
      variant === 'whatsapp'
        ? {
            flexShrink: 0,
            width: 44,
            height: 44,
            bgcolor: '#25D366',
            color: '#fff',
            '&:hover': { bgcolor: '#20bd5a' },
            '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' },
          }
        : { flexShrink: 0 };
    return (
      <Tooltip title="Gravar áudio">
        <span>
          <IconButton
            color={variant === 'whatsapp' ? 'default' : 'primary'}
            onClick={handleStart}
            disabled={disabled}
            size={size}
            sx={whatsappMicSx}
          >
            <MicIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: '999px',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)',
        border: '1px solid',
        borderColor: 'rgba(239, 68, 68, 0.25)',
        boxShadow: '0 1px 3px rgba(239, 68, 68, 0.12)',
        animation: `${slideIn} 0.18s ease-out`,
      }}
    >
      <Tooltip title="Descartar">
        <IconButton size="small" onClick={handleCancel} sx={{ color: '#ef4444', flexShrink: 0 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          bgcolor: '#ef4444',
          flexShrink: 0,
          animation: `${pulse} 1.4s ease-in-out infinite`,
        }}
      />

      <Typography
        variant="body2"
        sx={{
          color: '#b91c1c',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          minWidth: 38,
          flexShrink: 0,
        }}
      >
        {formatClock(seconds)}
      </Typography>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '3px',
          height: 24,
          overflow: 'hidden',
        }}
      >
        {WAVE_BARS.map((i) => (
          <Box
            key={i}
            sx={{
              width: 3,
              height: '100%',
              borderRadius: 2,
              transformOrigin: 'center',
              bgcolor: 'rgba(239, 68, 68, 0.65)',
              animation: `${wave} ${0.9 + (i % 4) * 0.18}s ease-in-out ${(i % 5) * 0.09}s infinite`,
            }}
          />
        ))}
      </Box>

      <Tooltip title="Enviar áudio">
        <IconButton
          onClick={handleSend}
          sx={{
            flexShrink: 0,
            color: '#fff',
            bgcolor: '#22c55e',
            width: 36,
            height: 36,
            '&:hover': { bgcolor: '#16a34a' },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default InboxAudioRecorder;
